import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, FileText, IndianRupee, Receipt, Link, Upload, Trash2, Save, Plus, Banknote, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user, hasTabAccess } = useAuth();
  const canEditInvoice = hasTabAccess('invoices') || user?.is_super_admin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [emailLinks, setEmailLinks] = useState([]);
  const [newEmailLink, setNewEmailLink] = useState('');
  const [status, setStatus] = useState('draft');

  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`, {
        withCredentials: true,
      });
      setInvoice(response.data);
      setRemarks(response.data.remarks || '');
      setEmailLinks(response.data.email_links || []);
      setStatus(response.data.status || 'draft');
    } catch (error) {
      toast.error('Failed to load invoice details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/invoices/${invoiceId}/details`,
        null,
        {
          params: { remarks, email_links: emailLinks, status },
          withCredentials: true,
        }
      );
      toast.success('Details saved successfully');
    } catch (error) {
      toast.error('Failed to save details');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmailLink = () => {
    if (newEmailLink.trim()) {
      setEmailLinks([...emailLinks, newEmailLink.trim()]);
      setNewEmailLink('');
    }
  };

  const handleRemoveEmailLink = (index) => {
    setEmailLinks(emailLinks.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/invoices/${invoiceId}/attachments`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded successfully');
      fetchInvoiceDetails();
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = async (filename) => {
    if (!window.confirm('Delete this attachment?')) return;

    try {
      await axios.delete(`${API}/invoices/${invoiceId}/attachments/${encodeURIComponent(filename)}`, {
        withCredentials: true,
      });
      toast.success('Attachment deleted');
      fetchInvoiceDetails();
    } catch (error) {
      toast.error('Failed to delete attachment');
      console.error(error);
    }
  };

  const downloadAttachment = async (attachment) => {
    // Attachments now live in object storage — stream them through the
    // backend instead of using the inline base64 data URL. Falls back
    // automatically for legacy attachments because the backend's
    // /attachments/{filename}/download endpoint handles both cases.
    try {
      const res = await axios.get(
        `${API}/invoices/${invoice.id}/attachments/${encodeURIComponent(attachment.filename)}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const blob = new Blob([res.data], { type: attachment.content_type || res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download attachment');
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Invoice not found</p>
        <Button className="mt-4" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Invoices
        </Button>
      </div>
    );
  }

  const outstandingAmount = (invoice.net_payable || 0) - (invoice.paid_amount || 0);

  return (
    <div data-testid="invoice-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
              {invoice.invoice_number}
              {(() => {
                const s = (status || 'draft').toLowerCase();
                const variantMap = {
                  draft: 'secondary',
                  approved: 'default',
                  final: 'default',
                  paid: 'success',
                  cancelled: 'destructive',
                };
                const labelMap = {
                  draft: 'Draft',
                  approved: 'Approved',
                  final: 'Final',
                  paid: 'Paid',
                  cancelled: 'Cancelled',
                };
                return (
                  <Badge variant={variantMap[s] || 'secondary'}>
                    {labelMap[s] || status}
                  </Badge>
                );
              })()}
            </h1>
            <p className="text-muted-foreground">{invoice.vendor_name}</p>
          </div>
        </div>
        {canEditInvoice && (
          <Button onClick={handleSaveDetails} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Information */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Number</p>
                  <p className="font-semibold">{invoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-semibold">{new Date(invoice.date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-semibold">{invoice.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-semibold">{invoice.department || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Budget Head</p>
                  <p className="font-semibold">{invoice.budget_head || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fiscal Year</p>
                  <p className="font-semibold">{invoice.fiscal_year || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Details */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Taxable Value</p>
                  <p className="text-xl font-bold">₹{invoice.taxable_value?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Net Payable</p>
                  <p className="text-xl font-bold text-primary">₹{invoice.net_payable?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Paid Amount</p>
                  <p className="text-xl font-bold text-green-600">₹{invoice.paid_amount?.toLocaleString('en-IN') || 0}</p>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-xl font-bold text-red-600">₹{outstandingAmount.toLocaleString('en-IN')}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">CGST:</span> ₹{invoice.cgst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">SGST:</span> ₹{invoice.sgst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">IGST:</span> ₹{invoice.igst?.toLocaleString('en-IN') || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">TDS:</span> ₹{invoice.tds?.toLocaleString('en-IN') || 0}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linked PO */}
          {invoice.po_details && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Linked Purchase Order
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-semibold text-lg">{invoice.po_details.po_number}</p>
                    <p className="text-sm text-muted-foreground">
                      Taxable Value: ₹{invoice.po_details.taxable_value?.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => navigate(`/purchase-orders/${invoice.po_id}`)}>
                    View PO
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Related Payments */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Payments ({invoice.payments?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoice.payments?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No payments recorded for this invoice</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 text-xs uppercase text-muted-foreground">
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-right py-2 px-3">Amount</th>
                        <th className="text-left py-2 px-3">Reference</th>
                        <th className="text-center py-2 px-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments?.map((payment, index) => (
                        <tr key={index} className="border-b border-border hover:bg-muted/30">
                          <td className="py-2 px-3 text-sm">{new Date(payment.date).toLocaleDateString()}</td>
                          <td className="py-2 px-3 text-right font-semibold text-green-600">
                            ₹{payment.amount?.toLocaleString('en-IN')}
                          </td>
                          <td className="py-2 px-3 text-sm font-mono">{payment.reference || '-'}</td>
                          <td className="py-2 px-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => navigate('/bank')}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={status} onValueChange={setStatus} disabled={!canEditInvoice}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Remarks */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Remarks</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Add remarks or notes..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                disabled={!canEditInvoice}
              />
            </CardContent>
          </Card>

          {/* Email Links */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                Email Links
              </CardTitle>
              <CardDescription>Link related email threads</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {emailLinks.map((link, index) => (
                <div key={index} className="flex items-center gap-2">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-sm text-primary hover:underline truncate"
                  >
                    {link}
                  </a>
                  {canEditInvoice && (
                    <Button size="icon" variant="ghost" onClick={() => handleRemoveEmailLink(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {canEditInvoice && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste email link..."
                    value={newEmailLink}
                    onChange={(e) => setNewEmailLink(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddEmailLink()}
                  />
                  <Button size="icon" onClick={handleAddEmailLink}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Attachments
              </CardTitle>
              <CardDescription>Upload proof documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {invoice.attachments?.map((att, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <button
                    onClick={() => downloadAttachment(att)}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {att.filename}
                  </button>
                  {canEditInvoice && (
                    <Button size="icon" variant="ghost" onClick={() => handleDeleteAttachment(att.filename)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {canEditInvoice && (
                <div>
                  <input
                    type="file"
                    id="invoice-attachment"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="invoice-attachment">
                    <Button variant="outline" className="w-full cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload File'}
                      </span>
                    </Button>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
